import React, { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    List,
    ListItem,
    ListItemText,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { RichTreeViewPro } from "@mui/x-tree-view-pro/RichTreeViewPro";
import { TreeItem2, TreeItem2Props } from "@mui/x-tree-view/TreeItem2";
import axiosInstance from "../AxiosInstance";
import ConfirmDialog from "./shared/ConfirmDialog";

const CUSTOMER_DRAG_TYPE = "application/x-abakus-customer-id";
const UNCATEGORISED_ID = "uncategorised";
const MAXIMUM_DEPTH = 3;

interface ChannelDto {
    channelId: number;
    name: string;
    parentChannelId: number | null;
    sortOrder: number;
    directCustomerCount: number;
    customerCount?: number;
    path: string;
    isEditable: boolean;
}

interface CustomerDto {
    customerId: number;
    name: string;
    channelId: number | null;
}

interface ChannelTreeItem {
    id: string;
    name: string;
    label: string;
    type: "channel" | "uncategorised";
    channelId: number | null;
    parentChannelId: number | null;
    directCustomerCount: number;
    customerCount: number;
    isEditable: boolean;
    children: ChannelTreeItem[];
}

type CustomerDropHandler = (customerId: number, channelId: number | null) => void;

const CustomerDropContext = createContext<CustomerDropHandler>(() => undefined);
const TreeItemDataContext = createContext<Map<string, ChannelTreeItem>>(new Map());

const DroppableTreeItem = forwardRef<HTMLLIElement, TreeItem2Props>(function DroppableTreeItem(
    props,
    ref
) {
    const assignCustomer = useContext(CustomerDropContext);
    const itemsById = useContext(TreeItemDataContext);
    const { itemId, label, onDragOver, onDrop, ...other } = props;
    const item = itemsById.get(itemId);

    return (
        <TreeItem2
            {...other}
            itemId={itemId}
            ref={ref}
            label={
                item ? (
                    <Box
                        component="span"
                        sx={{ display: "inline-flex", alignItems: "baseline", columnGap: 0.75 }}
                    >
                        <Box component="span">{label}</Box>
                        <Typography component="span" variant="body2" color="text.secondary">
                            ({item.customerCount})
                        </Typography>
                    </Box>
                ) : label
            }
            onDragOver={(event) => {
                if (event.dataTransfer.types.includes(CUSTOMER_DRAG_TYPE)) {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                }
                onDragOver?.(event);
            }}
            onDrop={(event) => {
                const customerId = Number(event.dataTransfer.getData(CUSTOMER_DRAG_TYPE));
                if (Number.isInteger(customerId) && customerId > 0) {
                    event.preventDefault();
                    event.stopPropagation();
                    assignCustomer(customerId, Number(itemId));
                }
                onDrop?.(event);
            }}
        />
    );
});

const buildTree = (channels: ChannelDto[]): ChannelTreeItem[] => {
    const byParent = new Map<number | null, ChannelDto[]>();
    channels.forEach((channel) => {
        const siblings = byParent.get(channel.parentChannelId) ?? [];
        siblings.push(channel);
        byParent.set(channel.parentChannelId, siblings);
    });
    byParent.forEach((siblings) =>
        siblings.sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name))
    );

    const toTreeItem = (channel: ChannelDto): ChannelTreeItem => {
        const children = (byParent.get(channel.channelId) ?? []).map(toTreeItem);
        const customerCount = channel.customerCount
            ?? channel.directCustomerCount + children.reduce((sum, child) => sum + child.customerCount, 0);
        return {
            id: String(channel.channelId),
            name: channel.name,
            label: `${channel.name} (${customerCount})`,
            type: "channel",
            channelId: channel.channelId,
            parentChannelId: channel.parentChannelId,
            directCustomerCount: channel.directCustomerCount,
            customerCount,
            isEditable: channel.isEditable,
            children
        };
    };

    return (byParent.get(null) ?? []).map(toTreeItem);
};

const flattenTree = (items: ChannelTreeItem[]): ChannelTreeItem[] =>
    items.flatMap((item) => [item, ...flattenTree(item.children)]);

const getSubtreeHeight = (item: ChannelTreeItem): number =>
    item.children.length === 0
        ? 1
        : 1 + Math.max(...item.children.map(getSubtreeHeight));

const getDepth = (itemId: string | null, items: ChannelTreeItem[]): number => {
    if (itemId === null) {
        return 0;
    }

    const flatItems = flattenTree(items);
    let item = flatItems.find((candidate) => candidate.id === itemId);
    let depth = 0;
    const visited = new Set<string>();
    while (item && !visited.has(item.id)) {
        visited.add(item.id);
        depth++;
        const parentChannelId = item.parentChannelId;
        item = parentChannelId === null
            ? undefined
            : flatItems.find((candidate) => candidate.channelId === parentChannelId);
    }
    return depth;
};

const getErrorMessage = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as { error?: string; message?: string } | string | undefined;
        if (typeof data === "string") {
            return data;
        }
        return data?.error ?? data?.message ?? "The channel hierarchy could not be updated.";
    }
    return "The channel hierarchy could not be updated.";
};

export default function ChannelsSettingsPage() {
    const [channels, setChannels] = useState<ChannelDto[]>([]);
    const [treeItems, setTreeItems] = useState<ChannelTreeItem[]>([]);
    const [customers, setCustomers] = useState<CustomerDto[]>([]);
    const [uncategorisedCount, setUncategorisedCount] = useState(0);
    const [selectedItemId, setSelectedItemId] = useState<string | null>(UNCATEGORISED_ID);
    const [expandedItems, setExpandedItems] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [createParentId, setCreateParentId] = useState<number | null | undefined>(undefined);
    const [newChannelName, setNewChannelName] = useState("");
    const [deleteOpen, setDeleteOpen] = useState(false);

    const itemsById = useMemo(() => {
        const map = new Map<string, ChannelTreeItem>();
        flattenTree(treeItems).forEach((item) => map.set(item.id, item));
        return map;
    }, [treeItems]);

    const selectedItem = useMemo((): ChannelTreeItem | undefined => {
        if (selectedItemId === UNCATEGORISED_ID) {
            return {
                id: UNCATEGORISED_ID,
                name: "Uncategorised",
                label: `Uncategorised (${uncategorisedCount})`,
                type: "uncategorised",
                channelId: null,
                parentChannelId: null,
                directCustomerCount: uncategorisedCount,
                customerCount: uncategorisedCount,
                isEditable: false,
                children: []
            };
        }

        return flattenTree(treeItems).find((item) => item.id === selectedItemId);
    }, [selectedItemId, treeItems, uncategorisedCount]);

    const loadChannels = useCallback(async () => {
        const [channelResponse, uncategorisedResponse] = await Promise.all([
            axiosInstance.get<ChannelDto[]>("/channels"),
            axiosInstance.get<CustomerDto[]>("/customers/uncategorised")
        ]);
        const nextTree = buildTree(channelResponse.data);
        setChannels(channelResponse.data);
        setUncategorisedCount(uncategorisedResponse.data.length);
        setTreeItems(nextTree);
        setExpandedItems(flattenTree(nextTree).map((item) => item.id));
    }, []);

    const loadCustomers = useCallback(async (itemId: string | null) => {
        if (!itemId) {
            setCustomers([]);
            return;
        }

        setLoadingCustomers(true);
        try {
            const url = itemId === UNCATEGORISED_ID
                ? "/customers/uncategorised"
                : `/customers/by-channel/${itemId}`;
            const response = await axiosInstance.get<CustomerDto[]>(url);
            setCustomers(response.data);
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setLoadingCustomers(false);
        }
    }, []);

    useEffect(() => {
        const initialize = async () => {
            setLoading(true);
            try {
                await loadChannels();
            } catch (requestError) {
                setError(getErrorMessage(requestError));
            } finally {
                setLoading(false);
            }
        };
        void initialize();
    }, [loadChannels]);

    useEffect(() => {
        void loadCustomers(selectedItemId);
    }, [loadCustomers, selectedItemId]);

    const refresh = useCallback(async () => {
        await Promise.all([loadChannels(), loadCustomers(selectedItemId)]);
    }, [loadChannels, loadCustomers, selectedItemId]);

    const assignCustomer = useCallback<CustomerDropHandler>(async (customerId, channelId) => {
        const previousCustomers = customers;
        setCustomers((current) => current.filter((customer) => customer.customerId !== customerId));
        try {
            await axiosInstance.put(`/customers/${customerId}/channel`, { channelId });
            setSuccess(channelId === null ? "Customer marked as uncategorised." : "Customer moved.");
            await refresh();
        } catch (requestError) {
            setCustomers(previousCustomers);
            setError(getErrorMessage(requestError));
        }
    }, [customers, refresh]);

    const createChannel = async () => {
        try {
            await axiosInstance.post("/channels", {
                name: newChannelName,
                parentChannelId: createParentId ?? null
            });
            setCreateParentId(undefined);
            setNewChannelName("");
            setSuccess("Channel created.");
            await refresh();
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        }
    };

    const renameChannel = async (itemId: string, newLabel: string) => {
        const channel = channels.find((candidate) => String(candidate.channelId) === itemId);
        if (!channel || channel.name === newLabel) {
            return;
        }

        try {
            await axiosInstance.put(`/channels/${itemId}/name`, { name: newLabel });
            setSuccess("Channel renamed.");
            await refresh();
        } catch (requestError) {
            setError(getErrorMessage(requestError));
            await loadChannels();
        }
    };

    const moveChannel = async (params: {
        itemId: string;
        newPosition: { parentId: string | null; index: number };
    }) => {
        const parentId = params.newPosition.parentId;
        if (parentId === UNCATEGORISED_ID) {
            return;
        }

        const parentChannelId = parentId == null ? null : Number(parentId);
        if (parentChannelId !== null && !Number.isInteger(parentChannelId)) {
            return;
        }

        try {
            await axiosInstance.put(`/channels/${params.itemId}/position`, {
                parentChannelId: parentChannelId,
                sortOrder: params.newPosition.index
            });
            setSuccess("Channel moved.");
            await refresh();
        } catch (requestError) {
            setError(getErrorMessage(requestError));
            await loadChannels();
        }
    };

    const deleteChannel = async () => {
        if (!selectedItem || selectedItem.type !== "channel") {
            return;
        }
        try {
            await axiosInstance.delete(`/channels/${selectedItem.channelId}`);
            setDeleteOpen(false);
            setSelectedItemId(UNCATEGORISED_ID);
            setSuccess("Channel deleted.");
            await refresh();
        } catch (requestError) {
            setDeleteOpen(false);
            setError(getErrorMessage(requestError));
        }
    };

    const canMoveItem = useCallback((params: {
        itemId: string;
        newPosition: { parentId: string | null; index: number };
    }) => {
        if (params.itemId === UNCATEGORISED_ID || params.newPosition.parentId === UNCATEGORISED_ID) {
            return false;
        }
        if (params.itemId === params.newPosition.parentId) {
            return false;
        }

        const flatItems = flattenTree(treeItems);
        const item = flatItems.find((candidate) => candidate.id === params.itemId);
        if (!item) {
            return false;
        }

        let ancestorId = params.newPosition.parentId;
        const visited = new Set<string>();
        while (ancestorId && !visited.has(ancestorId)) {
            if (ancestorId === params.itemId) {
                return false;
            }
            visited.add(ancestorId);
            const ancestor = flatItems.find((candidate) => candidate.id === ancestorId);
            ancestorId = ancestor?.parentChannelId == null ? null : String(ancestor.parentChannelId);
        }

        return getDepth(params.newPosition.parentId, treeItems) + getSubtreeHeight(item) <= MAXIMUM_DEPTH;
    }, [treeItems]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <CustomerDropContext.Provider value={assignCustomer}>
        <TreeItemDataContext.Provider value={itemsById}>
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom>Channels</Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                    Build up to three channel levels. Drag a channel between others to reorder, or onto
                    the middle of another channel to nest it. Drag customers onto a channel to categorise
                    them.
                </Typography>

                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
                    <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 2 }}>
                        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
                            <Button
                                startIcon={<AddIcon />}
                                onClick={() => setCreateParentId(null)}
                            >
                                Add root
                            </Button>
                            <Button
                                startIcon={<SubdirectoryArrowRightIcon />}
                                disabled={!selectedItem || selectedItem.type !== "channel" ||
                                    getDepth(selectedItem.id, treeItems) >= MAXIMUM_DEPTH ||
                                    !selectedItem.isEditable}
                                onClick={() => setCreateParentId(selectedItem?.channelId)}
                            >
                                Add child
                            </Button>
                            <Button
                                color="error"
                                startIcon={<DeleteIcon />}
                                disabled={!selectedItem || selectedItem.type !== "channel" ||
                                    !selectedItem.isEditable}
                                onClick={() => setDeleteOpen(true)}
                            >
                                Delete
                            </Button>
                        </Stack>
                        <Divider sx={{ mb: 1 }} />
                        <RichTreeViewPro
                            items={treeItems}
                            selectedItems={selectedItemId === UNCATEGORISED_ID ? null : selectedItemId}
                            onSelectedItemsChange={(_, itemId) => setSelectedItemId(itemId)}
                            expandedItems={expandedItems}
                            onExpandedItemsChange={(_, itemIds) => setExpandedItems(itemIds)}
                            getItemId={(item) => item.id}
                            getItemLabel={(item) => item.name}
                            isItemEditable={(item) => item.type === "channel" && item.isEditable}
                            onItemLabelChange={(itemId, label) => void renameChannel(itemId, label)}
                            itemsReordering
                            canMoveItemToNewPosition={canMoveItem}
                            onItemPositionChange={(params) => void moveChannel(params)}
                            experimentalFeatures={{
                                labelEditing: true,
                                itemsReordering: true,
                                indentationAtItemLevel: true
                            }}
                            slots={{ item: DroppableTreeItem }}
                            sx={{ minHeight: 280 }}
                        />
                        <Divider sx={{ my: 1 }} />
                        <Box
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedItemId(UNCATEGORISED_ID)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    setSelectedItemId(UNCATEGORISED_ID);
                                }
                            }}
                            onDragOver={(event) => {
                                if (event.dataTransfer.types.includes(CUSTOMER_DRAG_TYPE)) {
                                    event.preventDefault();
                                    event.dataTransfer.dropEffect = "move";
                                }
                            }}
                            onDrop={(event) => {
                                const customerId = Number(event.dataTransfer.getData(CUSTOMER_DRAG_TYPE));
                                if (Number.isInteger(customerId) && customerId > 0) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    void assignCustomer(customerId, null);
                                }
                            }}
                            sx={{
                                px: 1.5,
                                py: 1,
                                borderRadius: 1,
                                cursor: "pointer",
                                bgcolor: selectedItemId === UNCATEGORISED_ID ? "action.selected" : "transparent",
                                "&:hover": { bgcolor: "action.hover" }
                            }}
                        >
                            <Typography fontWeight={600}>
                                Uncategorised ({uncategorisedCount})
                            </Typography>
                        </Box>
                    </Paper>

                    <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 2 }}>
                        <Typography variant="h6">
                            {selectedItem?.label ?? "Select a channel"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Directly assigned customers. Drag a customer onto another channel in the tree.
                        </Typography>
                        <Divider />
                        {loadingCustomers ? (
                            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : customers.length === 0 ? (
                            <Typography color="text.secondary" sx={{ p: 2 }}>
                                No customers are directly assigned here.
                            </Typography>
                        ) : (
                            <List dense sx={{ maxHeight: 420, overflow: "auto" }}>
                                {customers.map((customer) => (
                                    <ListItem
                                        key={customer.customerId}
                                        draggable
                                        onDragStart={(event) => {
                                            event.dataTransfer.setData(
                                                CUSTOMER_DRAG_TYPE,
                                                String(customer.customerId)
                                            );
                                            event.dataTransfer.effectAllowed = "move";
                                        }}
                                        sx={{ cursor: "grab", borderBottom: 1, borderColor: "divider" }}
                                    >
                                        <ListItemText primary={customer.name} />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Paper>
                </Stack>
            </Box>

            <Dialog
                open={createParentId !== undefined}
                onClose={() => setCreateParentId(undefined)}
                fullWidth
                maxWidth="xs"
            >
                <Box
                    component="form"
                    onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
                        event.preventDefault();
                        if (newChannelName.trim()) {
                            void createChannel();
                        }
                    }}
                >
                    <DialogTitle>
                        {createParentId === null ? "Create root channel" : "Create child channel"}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Channel name"
                            value={newChannelName}
                            onChange={(event) => setNewChannelName(event.target.value)}
                            inputProps={{ maxLength: 100 }}
                            sx={{ mt: 1 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            type="button"
                            color="secondary"
                            onClick={() => setCreateParentId(undefined)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            color="primary"
                            disabled={!newChannelName.trim()}
                        >
                            Create
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>

            <ConfirmDialog
                open={deleteOpen}
                title="Delete channel?"
                message="The channel must have no child channels and no assigned customers."
                confirmLabel="Delete"
                onClose={() => setDeleteOpen(false)}
                onConfirm={() => void deleteChannel()}
            />

            <Snackbar
                open={Boolean(error)}
                autoHideDuration={6000}
                onClose={() => setError("")}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="error" onClose={() => setError("")}>{error}</Alert>
            </Snackbar>
            <Snackbar
                open={Boolean(success)}
                autoHideDuration={3000}
                onClose={() => setSuccess("")}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="success" onClose={() => setSuccess("")}>{success}</Alert>
            </Snackbar>
        </TreeItemDataContext.Provider>
        </CustomerDropContext.Provider>
    );
}
